import React from "react";
import { useState, useEffect } from "react";
import { KeyboardEvent } from "react";

import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import FormRowState from "../UtilityComponents/FormUtilityComponents/FormRowState";
import FormConfig from "../UtilityComponents/FormUtilityComponents/FormConfig";
import FormIsValid from "../UtilityComponents/FormUtilityComponents/FormIsValid";
import SymbolAutocomplete from "../UtilityComponents/SymbolUtilityComponents/SymbolAutocomplete";

import { Typography } from "@mui/material";
import Box from "@mui/material/Box";

import Collapse from "@mui/material/Collapse";
import ConfiguredForm from "../UtilityComponents/FormUtilityComponents/ConfiguredForm";

const paperPadding = 2;

type StockPlanDialogProps = {
  open: boolean;
  loading: boolean;
  handleCancel: () => void;
  isNew: boolean;
  handleAddSymbol: (symbol: string) => void;
  handleAddOrSave: (state: FormRowState[]) => void;
  symbol: string | undefined;
  setSymbol: (newSymbol: string | undefined) => void;
  symbolUnsubmitted: boolean;
  reeditSymbol: () => void;
  promptForSymbol: boolean;
  stockPlanFormConfig: FormConfig;
  setStockPlanFormConfig: (
    newConfig: (FormConfig | undefined) | ((oldConfig: FormConfig | undefined) => FormConfig | undefined)
  ) => void;
  stockPlanFormState: FormRowState[];
  setStockPlanFormState: (newState: FormRowState[] | ((oldState: FormRowState[]) => FormRowState[])) => void;
  handleDelete: () => void;
};

function StockPlanDialog(props: StockPlanDialogProps) {
  const [deleteLastClicked, setDeleteLastClicked] = useState<boolean | undefined>(undefined);

  const [refreshing, setRefreshing] = useState(false);

  const onSymbolPrompting = props.isNew && props.promptForSymbol && props.symbolUnsubmitted;
  const symbolJustSubmitted = props.isNew && props.promptForSymbol && !props.symbolUnsubmitted;
  const formValid = FormIsValid(props.stockPlanFormState, props.stockPlanFormConfig);

  function handleSubmit() {
    if (!props.loading && !refreshing) {
      if (onSymbolPrompting) {
        if (props.symbol) {
          props.handleAddSymbol(props.symbol);
        } else {
          return;
        }
      } else if (formValid) {
        setDeleteLastClicked(false);
        props.handleAddOrSave(props.stockPlanFormState);
      }
    }
  }

  function catchEnterKeyDownToSubmit(e: KeyboardEvent) {
    if (!props.loading && !refreshing && e.key === "Enter") {
      if (onSymbolPrompting) {
        if (props.symbol) {
          handleSubmit();
        } else {
          return;
        }
      } else if (formValid) {
        handleSubmit();
      }
    }
  }

  return (
    <Dialog
      open={props.open}
      onClose={() => {
        if (!refreshing) {
          props.handleCancel();
        }
      }}
      maxWidth="md"
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle>
        {props.isNew
          ? props.promptForSymbol
            ? "Which company stock do you own or earn?"
            : "Add Stock Plan (4 min)"
          : "Edit Stock Plan"}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "row" }}>
        <Stack sx={{ flexGrow: 1, px: paperPadding, paddingTop: 0, gap: 2 }}>
          {props.promptForSymbol ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ pb: 2 }}>
                With this, we will automatically generate the shortest form for you to enter your stock plan information
                and turn those complicated numbers and words into easy-to-understand, interactive charts and plans.
              </Typography>
              <SymbolAutocomplete
                setSymbol={(newSymbol: string | null) => props.setSymbol(newSymbol || undefined)}
                symbol={props.symbol || null}
                TextFieldProps={{
                  autoFocus: true,
                  fullWidth: true,
                  label: "Stock Plan Company or Ticker Symbol",
                  // helperText: props.symbolUnsubmitted
                  //   ? "With this, we will automatically generate the shortest form for you to enter your stock plan information and turn those complicated numbers and words into easy-to-understand, interactive charts and plans."
                  //   : null,
                  disabled: !props.symbolUnsubmitted || props.loading || refreshing,
                  onKeyDown: catchEnterKeyDownToSubmit,
                  inputProps: { tabIndex: 0 },
                }}
              />
            </Box>
          ) : null}
          <Collapse in={!props.promptForSymbol || !props.symbolUnsubmitted}>
            <ConfiguredForm
              config={props.stockPlanFormConfig}
              setConfig={props.setStockPlanFormConfig}
              refreshing={refreshing}
              setRefreshing={setRefreshing}
              disabled={props.loading || refreshing}
              // disableFirstRow={!props.isNew}
              state={props.stockPlanFormState}
              setState={props.setStockPlanFormState}
              canSubmit={
                FormIsValid(props.stockPlanFormState, props.stockPlanFormConfig) &&
                !props.loading &&
                !refreshing &&
                !(props.isNew && props.promptForSymbol && props.symbolUnsubmitted)
              }
              handleSubmit={handleSubmit}
              catchEnterKeyDownToSubmit={catchEnterKeyDownToSubmit}
            />
          </Collapse>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button
          color="error"
          startIcon={symbolJustSubmitted ? <ArrowBackIcon /> : <CloseIcon />}
          onClick={symbolJustSubmitted ? props.reeditSymbol : props.handleCancel}
          disabled={props.loading || refreshing}
          tabIndex={8}
        >
          {symbolJustSubmitted ? "Back" : "Cancel"}
        </Button>
        {props.isNew ? null : (
          <LoadingButton
            variant="contained"
            color="error"
            loading={props.loading && deleteLastClicked === true}
            startIcon={<DeleteIcon />}
            disabled={props.isNew || props.loading || refreshing}
            onClick={() => {
              setDeleteLastClicked(true);
              props.handleDelete();
            }}
            tabIndex={9}
          >
            Delete
          </LoadingButton>
        )}
        <LoadingButton
          variant="contained"
          loading={props.loading && deleteLastClicked === false}
          endIcon={onSymbolPrompting ? <ArrowForwardIcon /> : null}
          startIcon={onSymbolPrompting ? null : props.isNew ? <AddIcon /> : <SaveIcon />}
          disabled={
            props.loading || (onSymbolPrompting && !props.symbol) || (!onSymbolPrompting && !formValid) || refreshing
          }
          onClick={handleSubmit}
          tabIndex={7}
        >
          {onSymbolPrompting ? "Next" : props.isNew ? "Add" : "Save"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default StockPlanDialog;
